/**
 * 服务端专用 Fetch 请求模块
 * 用于 Next.js getServerSideProps / getInitialProps 等服务端场景
 */

import APP from './api';

// ============ 类型定义 ============

export interface ServerRequestOptions {
  /** 请求方法 */
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  /** API 路径 (相对于 baseUrl) */
  url: string;
  /** GET 请求的查询参数 */
  params?: Record<string, any>;
  /** POST/PUT 请求的 body 数据 */
  data?: Record<string, any>;
  /** 自定义请求头 */
  headers?: Record<string, string>;
  /** 自定义 baseUrl，默认使用 canvas baseUrl */
  baseUrl?: string;
  /** 请求超时时间 (ms) */
  timeout?: number;
  /** 原始 cookie 字符串（从 context.req.headers.cookie 获取） */
  cookie?: string;
  /** 国家代码（从 context.req.countryCode 获取，由 server.ts 通过 IP 解析） */
  countryCode?: string;
}

export interface ServerResponse<T = any> {
  /** 响应数据 */
  data: T;
  /** HTTP 状态码 */
  status: number;
  /** 状态文本 */
  statusText: string;
  /** 响应头 */
  headers: Headers;
  /** 请求配置 */
  config: ServerRequestOptions;
}

// ============ 工具函数 ============

/**
 * 解析原始 cookie 字符串为键值对
 * @param cookieStr 原始 cookie 字符串，如 "key1=value1; key2=value2"
 */
function parseCookies(cookieStr?: string): Record<string, string> {
  if (!cookieStr) return {};
  return cookieStr.split(';').reduce<Record<string, string>>((acc, pair) => {
    const [key, ...rest] = pair.trim().split('=');
    if (key) {
      acc[key.trim()] = decodeURIComponent(rest.join('='));
    }
    return acc;
  }, {});
}

/**
 * 判断是否为海外用户
 * @param countryCode 国家代码
 * @returns 是否为海外
 */
function getIsOverseaByCountryCode(countryCode?: string): boolean {
  if (!countryCode) return true;
  const mainlandCode = ['CN', 'TW', 'HK', 'MO'];
  return !mainlandCode.includes(countryCode.toUpperCase());
}

/**
 * 根据国家代码获取默认语言代码
 * 海外用户默认英文，国内用户默认简体中文
 * @param countryCode 国家代码
 * @returns 语言代码
 */
function getDefaultLangCodeByCountryCode(countryCode?: string): string {
  return getIsOverseaByCountryCode(countryCode) ? 'en' : 'zh-Hans';
}

/**
 * 获取服务端请求的基础 URL（处理协议相对 URL）
 * @param baseUrl 原始 baseUrl，可能是协议相对 URL (//xxx.com)
 * @returns 完整的 URL（带 https 协议）
 */
export function getServerBaseUrl(baseUrl: string): string {
  if (baseUrl.startsWith('//')) {
    return `https:${baseUrl}`;
  }
  return baseUrl;
}

export interface ServerCommonParamsOptions {
  /** 原始 cookie 字符串（来自 context.req.headers.cookie） */
  cookie?: string;
  /** 国家代码（来自 server.ts 中通过 IP 解析获取） */
  countryCode?: string;
}

/**
 * 获取服务端请求的公共参数（与画布 `@meitu/roboneo-web` GET 请求的 `commonParams` 对齐）
 * - client_id / client_language：画布网关必备
 * - gnum：与客户端 `Cookies.get('gid')` → 画布参数 `gnum` 一致，SSR 仅从 cookie `gid` 解析
 * - country_code：`fetchOverseaMenus` 等传入的 IP 解析国家码，与客户端 `currentCountryCode` 一致
 * @param options 配置选项，包含 cookie 和 countryCode
 */
export function getServerCommonParams(options?: ServerCommonParamsOptions | string): Record<string, string> {
  // 兼容旧的调用方式：直接传 cookie 字符串
  const opts: ServerCommonParamsOptions = typeof options === 'string'
    ? { cookie: options }
    : (options || {});

  const { cookie, countryCode } = opts;
  const cookies = parseCookies(cookie);
  // console.log('parseCookies cookies', cookies);
  // 优先使用 cookie 中的语言设置，否则根据国家代码获取默认语言（海外 en，国内 zh-Hans）
  const langCode = cookies.lang || getDefaultLangCodeByCountryCode(countryCode);
  // console.log('langCode', langCode);

  /** 埋点/DeviceId，与 `@/utils` getGid 使用的 cookie 键一致 */
  const gnumFromCookie = cookies.gid?.trim() || '';

  const common: Record<string, string> = {
    client_id: process.env.NEXT_PUBLIC_REBONEO_CLIENT_ID || '',
    client_language: langCode,
  };
  if (gnumFromCookie) {
    common.gnum = gnumFromCookie;
  }
  if (countryCode) {
    common.country_code = countryCode;
  }
  return common;
}

/**
 * 构建完整的请求 URL（带公共参数）
 */
function buildRequestUrl(
  baseUrl: string,
  path: string,
  queryParams: Record<string, any> = {},
  commonParamsOptions?: ServerCommonParamsOptions,
): string {
  const fullBaseUrl = getServerBaseUrl(baseUrl);
  const url = new URL(`${fullBaseUrl}${path}`);
  const commonParams = getServerCommonParams(commonParamsOptions);
  console.log('commonParams', commonParams);

  // 添加公共参数
  Object.entries(commonParams).forEach(([key, value]) => {
    if (value) {
      url.searchParams.set(key, value);
    }
  });

  // 添加查询参数
  Object.entries(queryParams).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      url.searchParams.set(key, String(value));
    }
  });

  return url.toString();
}

// ============ 核心请求函数 ============

/**
 * 服务端 Fetch 请求
 * 自动处理协议相对 URL、公共参数、超时等
 */
export async function serverFetch<T = any>(
  options: ServerRequestOptions,
): Promise<ServerResponse<T>> {
  const {
    method = 'GET',
    url,
    params = {},
    data,
    headers = {},
    baseUrl,
    timeout = 30000,
    cookie,
    countryCode,
  } = options;
  const isOverseaServer = countryCode ? getIsOverseaByCountryCode(countryCode) : false;
  const requestBaseUrl = baseUrl || APP.roboneo.baseCanvasURL(isOverseaServer);

  // 构建完整 URL
  const fullUrl = buildRequestUrl(
    requestBaseUrl,
    url,
    method === 'GET' ? params : {},
    { cookie, countryCode },
  );

  // 构建请求配置
  const fetchOptions: RequestInit = {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...headers,
    },
  };

  // 非 GET 请求添加 body
  if (method !== 'GET' && data) {
    const commonParams = getServerCommonParams({ cookie, countryCode });
    fetchOptions.body = JSON.stringify({
      ...commonParams,
      ...data,
    });
  }

  // 创建超时 AbortController
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  fetchOptions.signal = controller.signal;

  try {
    const res = await fetch(fullUrl, fetchOptions);
    clearTimeout(timeoutId);

    const responseData = await res.json();

    return {
      data: responseData,
      status: res.status,
      statusText: res.statusText,
      headers: res.headers,
      config: options,
    };
  } catch (error: any) {
    clearTimeout(timeoutId);

    if (error.name === 'AbortError') {
      throw new Error(`Request timeout after ${timeout}ms: ${url}`);
    }
    throw error;
  }
}

// ============ 便捷方法 ============

/**
 * 服务端 GET 请求
 */
export async function serverGet<T = any>(
  url: string,
  params?: Record<string, any>,
  options?: Omit<ServerRequestOptions, 'method' | 'url' | 'params'>,
): Promise<ServerResponse<T>> {
  return serverFetch<T>({
    ...options,
    method: 'GET',
    url,
    params,
  });
}

/**
 * 服务端 POST 请求
 */
export async function serverPost<T = any>(
  url: string,
  data?: Record<string, any>,
  options?: Omit<ServerRequestOptions, 'method' | 'url' | 'data'>,
): Promise<ServerResponse<T>> {
  return serverFetch<T>({
    ...options,
    method: 'POST',
    url,
    data,
  });
}

// ============ 默认导出 ============

const serverRequest = {
  fetch: serverFetch,
  get: serverGet,
  post: serverPost,
};

export default serverRequest;
